import { useEffect, useMemo, useRef, useState } from "react";
import { DatePicker, Select } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import uPlot, { type AlignedData, type Options } from "uplot";
import "uplot/dist/uPlot.min.css";
import dayjs, { type Dayjs } from "dayjs";
import { api, type SensorSeries } from "../api";
import { useGeoSettings } from "../contexts/GeoSettingsContext";
import { useTheme } from "../contexts/ThemeContext";

// ChartSeries is the unified internal model. Whether the data came
// from /series/sensors (one row per sensor, in/out only) or
// /series/ifaces (one row per (sensor, ifindex, dir), with envelope),
// we normalise into the same shape so the renderer has a single path.
//
// For the simpler total/nix/ixp views, in_max === in_mean and
// out_max === out_mean (no envelope information). For the iface view
// the values genuinely differ — letting the chart draw a smooth
// fill at the mean level with sharp peak outlines on top, matching
// Wansight's per-interface bandwidth chart style.
interface ChartSeries {
  id: string;
  label: string;
  color: string;
  points: {
    t: number;
    inMean: number; inMax: number;
    outMean: number; outMax: number;
  }[];
}

// CUSTOM_RANGE_VALUE is the sentinel emitted by the time-range Select
// when the operator picks "Custom range...". It's intentionally negative
// so it can never collide with a real `seconds` value (always positive
// in RANGES). When this value is active the Select stops driving the
// query — the DatePicker.RangePicker takes over via the `customRange`
// state below.
const CUSTOM_RANGE_VALUE = -1;

export type ViewMode = "total" | "nix" | "ixp";

// VIEW_OPTIONS_ALL is the static catalog. The component filters this
// down at render time based on geo settings — when the operator has
// disabled NIX/IXP collection in Settings → Geo, the corresponding
// `geo` measurement is empty, so showing those options would render a
// blank chart and confuse the operator (was the case in the screenshot
// audit on 2026-04-27 — operator turned both flags off, IXP option
// stayed in the dropdown, picking it gave a flat chart that looked
// like a sflow bug instead of a correct "no data" answer).
//
// Total is always available — reads from the `ip` measurement which
// is populated regardless of geo flags.
const VIEW_OPTIONS_ALL: { label: string; value: ViewMode }[] = [
  { label: "Total", value: "total" },
  { label: "NIX (Domestic)", value: "nix" },
  { label: "IXP (International)", value: "ixp" },
];

// ── helpers ─────────────────────────────────────────────────────────────────

function fmtBps(v: number | null): string {
  if (v == null || isNaN(v)) return "—";
  const a = Math.abs(v);
  if (a >= 1e9) return (v / 1e9).toFixed(1) + " G";
  if (a >= 1e6) return (v / 1e6).toFixed(1) + " M";
  if (a >= 1e3) return (v / 1e3).toFixed(1) + " k";
  return String(Math.round(v));
}

// pickAxisUnit chooses one unit (G/M/k) for the WHOLE Y-axis so every
// tick label aligns visually — Wansight's chart picks "G" once and writes
// "0.5 G", "1.0 G", "2.5 G" rather than mixing "500 M" + "1 G". The
// unit is driven by the largest absolute tick value.
function pickAxisUnit(maxAbs: number): { div: number; suffix: string } {
  if (maxAbs >= 1e9) return { div: 1e9, suffix: " G" };
  if (maxAbs >= 1e6) return { div: 1e6, suffix: " M" };
  if (maxAbs >= 1e3) return { div: 1e3, suffix: " k" };
  return { div: 1, suffix: "" };
}

// fmtBpsAxis formats a tick value using the chosen axis unit. Drops the
// trailing ".0" for whole numbers so labels are clean ("200 M" not
// "200.0 M"); fractional values keep one decimal ("1.5 G"). Negative sign
// is preserved — the chart shows in/out as +/-.
function fmtBpsAxis(v: number, unit: { div: number; suffix: string }): string {
  if (v === 0) return "0" + unit.suffix;
  return (v / unit.div).toFixed(1).replace(/\.0$/, "") + unit.suffix;
}

// fmtAgo turns a positive duration in seconds into a short readable
// label like "12s", "3m", "1h 4m". Used by the data-stale banner so the
// operator can tell at a glance how far behind the live edge is.
function fmtAgo(secs: number): string {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// Wansight-style palette: dark red for first sensor, then alternating warm tones
const PALETTE = [
  "#a01010", // dark red (Wansight primary)
  "#d04020", // dark orange-red
  "#1060c0", // blue
  "#208040", // green
  "#7030A0", // purple
  "#c08000", // gold
  "#007090", // teal
  "#900060", // magenta
];

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// trimTrailingEmpty drops up to MAX_TRIM_BINS trailing time points where every
// sensor reads zero. NetFlow sensors in deferred-flush mode write each bin to
// TSDB only after a 60 s late-fragment lookback elapses; the rightmost ~12
// bins of a fresh query are therefore zeros despite the network actually
// being busy. Hiding them keeps the chart anchored to the last bin with real
// data instead of stretching the time axis into an apparent dead zone.
//
// We cap the trim at 12 bins (60 s at 5 s cadence) so a real outage longer
// than 60 s still appears on the chart as zero traffic, not a clipped axis.
function trimTrailingEmpty(sensors: ChartSeries[]): ChartSeries[] {
  if (sensors.length === 0 || sensors[0].points.length === 0) return sensors;
  const MAX_TRIM_BINS = 12;
  const len = sensors[0].points.length;
  let trimAt = len;
  for (let i = len - 1; i >= 0 && len - trimAt < MAX_TRIM_BINS; i--) {
    const allZero = sensors.every(
      (s) => (s.points[i]?.inMax ?? 0) === 0 && (s.points[i]?.outMax ?? 0) === 0
    );
    if (!allZero) break;
    trimAt = i;
  }
  if (trimAt === len) return sensors;
  return sensors.map((s) => ({ ...s, points: s.points.slice(0, trimAt) }));
}

// sensorsToChartSeries maps the API's per-sensor envelope rows into
// the chart's internal ChartSeries shape. The wire shape carries
// in_mean/in_max/out_mean/out_max per bin; we just rename to camelCase
// for the renderer.
function sensorsToChartSeries(rows: SensorSeries[]): ChartSeries[] {
  return rows.map((s) => ({
    id: s.sensor_id,
    label: s.name,
    color: s.color,
    points: s.points.map((p) => ({
      t: p.t,
      inMean: p.in_mean,
      inMax: p.in_max,
      outMean: p.out_mean,
      outMax: p.out_max,
    })),
  }));
}

// ── presets ──────────────────────────────────────────────────────────────────

const RANGES: { label: string; value: number }[] = [
  { label: "Last 5 minutes", value: 5 * 60 },
  { label: "Last 10 minutes", value: 10 * 60 },
  { label: "Last 15 minutes", value: 15 * 60 },
  { label: "Last Hour", value: 60 * 60 },
  { label: "Last 3 Hours", value: 3 * 60 * 60 },
  { label: "Last 6 Hours", value: 6 * 60 * 60 },
  { label: "Last 12 Hours", value: 12 * 60 * 60 },
  { label: "Last Day", value: 24 * 60 * 60 },
  { label: "Last Week", value: 7 * 24 * 60 * 60 },
  { label: "Last Month", value: 30 * 24 * 60 * 60 },
  // Custom range — when picked, the DatePicker.RangePicker beside this
  // Select takes over. Auto-refresh is forced off while custom is
  // active because the window is anchored to absolute timestamps and
  // re-fetching on a timer would render the same data over and over.
  { label: "Custom range…", value: CUSTOM_RANGE_VALUE },
];

const REFRESHES: { label: string; value: number }[] = [
  { label: "off", value: 0 },
  { label: "5 seconds", value: 5_000 },
  { label: "30 seconds", value: 30_000 },
  { label: "1 minute", value: 60_000 },
  { label: "5 minutes", value: 5 * 60_000 },
];

// chartColors picks axis/grid colours per effective theme. uPlot draws
// to canvas so CSS vars are not directly usable — we resolve them at
// chart-init time and force a re-init when the theme flips.
function chartColors(effective: "light" | "dark") {
  if (effective === "light") {
    return {
      axisStroke: "#5d6b7e", // matches --muted, readable but soft
      gridStroke: "#d6dee8", // matches --line, low contrast
    };
  }
  return {
    axisStroke: "#b9c2dc", // navy-light text
    gridStroke: "#26334d", // muted line
  };
}

// ── component ────────────────────────────────────────────────────────────────

interface Props {
  seconds?: number;
  refreshMs?: number;
  height?: number;
  /**
   * When set, the chart is locked to a single sFlow source: only that
   * sensor's band is drawn and the in-toolbar Sensor picker is hidden.
   * Used by the per-sensor dashboard. When undefined (the global widget)
   * a "Sensor" dropdown lets the operator filter to one source or All.
   */
  sensorId?: number;
  /**
   * Notifies the parent whenever the effective scope (View + Sensor)
   * changes, so sibling widgets like TotalsBar can track the same scope.
   * `sensor` is the effective source (locked prop or in-widget picker;
   * undefined = all sources). Must be a stable callback (useCallback).
   */
  onScopeChange?: (view: ViewMode, sensor: number | undefined) => void;
}

export default function LiveChart({
  seconds: initialSeconds = 600,
  refreshMs: initialRefresh = 5000,
  height = 240,
  sensorId,
  onScopeChange,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const plotRef = useRef<uPlot | null>(null);
  // Tracks the last sensor-id set rendered into the picker so we only push
  // new options when the set changes (avoids a re-render every poll).
  const lastOptKey = useRef("");
  // Signature (id:color:label per series) of the currently-built plot, so we
  // can rebuild when the source identity/colour changes even if the series
  // count stays the same (per-sensor dashboard navigation).
  const lastSeriesSig = useRef("");
  const [empty, setEmpty] = useState(false);
  const [seconds, setSeconds] = useState(initialSeconds);
  const [refreshMs, setRefreshMs] = useState(initialRefresh);
  const [nonce, setNonce] = useState(0);
  const [view, setView] = useState<ViewMode>("total");

  // sensorFilter is the in-widget Sensor picker state for the GLOBAL chart
  // (undefined = All sources). Ignored when the `sensorId` prop is set (the
  // per-sensor dashboard locks the scope). sensorOpts is the dropdown's
  // option list, derived from the per-sensor series the API already returns
  // — no extra request needed.
  const [sensorFilter, setSensorFilter] = useState<number | undefined>(undefined);
  const [sensorOpts, setSensorOpts] = useState<{ label: string; value: number }[]>([]);
  // Effective scope: the locked prop wins; otherwise the picker; undefined = All.
  const scopeSensor = sensorId ?? sensorFilter;

  // Report the effective scope upward so siblings (TotalsBar) follow the
  // same View + Sensor the operator picked here. Fires on mount and on any
  // change; onScopeChange must be memoised by the parent to avoid a loop.
  useEffect(() => {
    onScopeChange?.(view, scopeSensor);
  }, [view, scopeSensor, onScopeChange]);

  // Sync local state when parent props change. useState only honours the
  // initialiser on mount; without these effects, a parent prop change
  // (e.g. operator updating Default Refresh in Settings) would be
  // ignored — the chart would keep polling at whatever rate it had on
  // first render.
  useEffect(() => { setSeconds(initialSeconds); }, [initialSeconds]);
  useEffect(() => { setRefreshMs(initialRefresh); }, [initialRefresh]);

  // Theme-aware axis colours; re-init the chart when the user toggles
  // because uPlot bakes colours into the canvas at construction time.
  const { effective } = useTheme();
  const colors = chartColors(effective);

  // customRange holds the absolute time window when the operator picks
  // "Custom range…" from the time-range Select. null = relative mode is
  // active (use `seconds` against now()). When non-null, both API calls
  // pass the absolute window and refresh is suppressed (no point
  // re-fetching a fixed historical window).
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);

  // VIEW_OPTIONS is filtered by geo settings — see comment above the
  // static catalog. When the operator toggles a flag off in Settings
  // → Geo, the corresponding option disappears from the dropdown
  // within ~5 s (next GeoSettingsContext refresh), and if the chart
  // was currently displaying that view we auto-fall-back to "total"
  // (the useEffect below).
  const { settings: geo } = useGeoSettings();
  const VIEW_OPTIONS = useMemo(() => {
    return VIEW_OPTIONS_ALL.filter((o) => {
      if (o.value === "nix") return geo.nix_enabled;
      if (o.value === "ixp") return geo.ixp_enabled;
      // "total" + "iface" always available
      return true;
    });
  }, [geo.nix_enabled, geo.ixp_enabled]);

  // Auto-recover when the operator disables a flag while the chart is
  // sitting on that view. Without this the chart would keep firing
  // /api/v1/series/geo?class=ixp queries that return zero rows and
  // render a blank chart — exactly the "tại sao chart trống" bug in
  // the parity-plan audit.
  useEffect(() => {
    if (!VIEW_OPTIONS.some((o) => o.value === view)) {
      setView("total");
    }
  }, [VIEW_OPTIONS, view]);
  // lastDataAt is the unix-seconds timestamp of the right-most non-zero
  // bin in the trimmed data. The "sensor down" banner reads it against
  // the wall clock so the operator sees the data freshness directly
  // (Bug 4 in the parity plan). null = never received data, we render
  // the existing "no data yet" overlay instead.
  const [lastDataAt, setLastDataAt] = useState<number | null>(null);
  // staleNonce ticks once per second to force re-renders so the banner's
  // age string ("Last data: 47s ago") updates even when no fetch
  // happens (network blip, sensor stopped exporting). Cheap — a single
  // setState every second across the component lifetime.
  const [, setStaleNonce] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setStaleNonce((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;

    // Convert ChartSeries[] to uPlot AlignedData.
    //
    // Layout per series: [in_max_line, in_mean_fill, out_mean_fill_neg, out_max_line_neg]
    // → 4 columns per ChartSeries.
    //
    // Why this column order: uPlot draws series in array order. We
    // want the FILL (mean) under the LINE (max) so peaks visibly poke
    // out above the smoothed area. Fill columns must be drawn AFTER
    // the line columns of the previous series to avoid stacking
    // artefacts between sensors.
    //
    // Out values are negated so they appear below the zero line —
    // matches Wansight's "in above / out below" convention.
    function toAligned(sensors: ChartSeries[]): AlignedData {
      if (sensors.length === 0) return [[], []];

      // Build a union of all timestamps (should all share the same
      // fill(0) grid, but union-merge to be defensive).
      const tsSet = new Set<number>();
      for (const s of sensors) for (const p of s.points) tsSet.add(p.t);
      const ts = Array.from(tsSet).sort((a, b) => a - b);

      const data: AlignedData = [ts];
      for (const sensor of sensors) {
        const byT = new Map(sensor.points.map((p) => [p.t, p]));
        const inMax: (number | null)[] = [];
        const inMean: (number | null)[] = [];
        const outMean: (number | null)[] = [];
        const outMax: (number | null)[] = [];
        for (const t of ts) {
          const p = byT.get(t);
          if (p) {
            inMax.push(p.inMax);
            inMean.push(p.inMean);
            outMean.push(-p.outMean);
            outMax.push(-p.outMax);
          } else {
            inMax.push(null); inMean.push(null);
            outMean.push(null); outMax.push(null);
          }
        }
        data.push(inMax, inMean, outMean, outMax);
      }
      return data;
    }

    // Build uPlot series config — 4 series per ChartSeries:
    //
    //   1. {label} in max  — outline line above mean fill
    //   2. {label} in mean — filled area (smooth average level)
    //   3. {label} out mean — filled area below zero
    //   4. {label} out max — outline line below mean fill
    //
    // The fills use 0.5 alpha (transparent) so the line on top stays
    // visible. Line width is 0.8 to keep it crisp without visually
    // overpowering the fill. Matches Wansight's per-interface chart
    // appearance: smooth dark-red fill with sharp peak outlines.
    function buildSeries(sensors: ChartSeries[]): Options["series"] {
      const base: Options["series"] = [{}]; // x
      sensors.forEach((s, i) => {
        const color = (s.color && s.color !== "") ? s.color : PALETTE[i % PALETTE.length];
        const fillColor = hexToRgba(color, 0.45);
        base.push(
          // in max — outline line on top of the in fill (peak spike).
          {
            label: `${s.label} in max`,
            stroke: color,
            width: 0.8,
            points: { show: false },
            value: (_u, v) => fmtBps(v),
          },
          // in mean — smooth filled area at the average level.
          {
            label: `${s.label} in`,
            stroke: hexToRgba(color, 0.7),
            fill: fillColor,
            width: 0.5,
            points: { show: false },
            value: (_u, v) => fmtBps(v),
          },
          // out mean — filled area below zero.
          {
            label: `${s.label} out`,
            stroke: hexToRgba(color, 0.7),
            fill: fillColor,
            width: 0.5,
            points: { show: false },
            value: (_u, v) => fmtBps(v == null ? null : -v),
          },
          // out max — outline line below the out fill (peak dip).
          {
            label: `${s.label} out max`,
            stroke: color,
            width: 0.8,
            points: { show: false },
            value: (_u, v) => fmtBps(v == null ? null : -v),
          }
        );
      });
      return base;
    }

    async function load() {
      try {
        // Build the time-window argument. When a custom range is set we
        // pass absolute unix-second bounds; otherwise the relative
        // `seconds` window. Server resolves which to use — see
        // parseTimeWindow in internal/web/series.go.
        const range = customRange
          ? { start: customRange[0].unix(), end: customRange[1].unix() }
          : undefined;

        // Fetch one row per sensor with envelope (mean+max) per bin
        // so the chart can draw a smooth fill at the average level
        // with peak outlines on top. Total view reads from the `ip`
        // measurement; nix/ixp from `geo`.
        const rows: SensorSeries[] =
          view === "total"
            ? await api.seriesSensors(seconds, range)
            : await api.seriesGeo(seconds, view, range);
        if (cancelled) return;

        // Refresh the Sensor-picker options from the rows the API returned
        // (one per source). Only update state when the id set actually
        // changed so the toolbar doesn't re-render every poll.
        const optKey = rows.map((r) => r.sensor_id).join(",");
        if (optKey !== lastOptKey.current) {
          lastOptKey.current = optKey;
          setSensorOpts(rows.map((r) => ({ label: r.name, value: Number(r.sensor_id) })));
        }

        // Scope to a single source when locked (prop) or picked (dropdown).
        const scopedRows =
          scopeSensor != null
            ? rows.filter((r) => Number(r.sensor_id) === scopeSensor)
            : rows;

        let sensors = sensorsToChartSeries(scopedRows);
        sensors = trimTrailingEmpty(sensors);

        const hasData = sensors.some((s) =>
          s.points.some((p) => p.inMax > 0 || p.outMax > 0)
        );
        setEmpty(!hasData);

        // Capture the timestamp of the right-most bin that actually
        // carried data, across every sensor. The banner uses this to
        // tell stale data apart from the legitimate NetFlow export
        // delay — see plan §Bug 4. Trim already discarded trailing
        // all-zero bins so the last point IS data-bearing.
        let latest = 0;
        for (const s of sensors) {
          for (const p of s.points) {
            if ((p.inMax > 0 || p.outMax > 0) && p.t > latest) {
              latest = p.t;
            }
          }
        }
        setLastDataAt(latest > 0 ? latest : null);

        const data = toAligned(sensors);

        // Rebuild if the series count OR the series identity/colour changed.
        // Each ChartSeries contributes 4 uPlot series (in_max line, in_mean
        // fill, out_mean fill, out_max line) → +1 for x. The signature guards
        // the case where the COUNT stays the same but the source (and its
        // colour) changes — e.g. navigating between per-sensor dashboards,
        // each of which has exactly one series: without this the plot keeps
        // the first source's colour for every subsequent one.
        const expectedSeriesCount = sensors.length * 4 + 1;
        const seriesSig = sensors.map((s) => `${s.id}:${s.color}:${s.label}`).join("|");
        if (
          plotRef.current &&
          (plotRef.current.series.length !== expectedSeriesCount ||
            seriesSig !== lastSeriesSig.current)
        ) {
          plotRef.current.destroy();
          plotRef.current = null;
        }

        if (!plotRef.current && ref.current) {
          const opts: Options = {
            width: ref.current.clientWidth,
            height,
            // Top padding gives the topmost Y-axis tick label vertical
            // room inside the canvas. Without this uPlot draws "1.5 G"
            // anchored to y=0 of the plot area and the upper half of the
            // glyph spills above the plot, which then gets clipped by the
            // toolbar above the chart container. 10px ≈ half a label.
            padding: [10, 0, 0, 0],
            cursor: { drag: { x: false, y: false } },
            legend: { show: true, live: true },
            scales: {
              x: { time: true },
              y: {
                auto: true,
                // Add 10% breathing room to top/bottom of Y range so the
                // topmost tick label (e.g. "1.5 G") doesn't get visually
                // clipped by the toolbar above the chart, and the bottom
                // peak doesn't kiss the X-axis. Without this, uPlot snaps
                // the data peak right to the plot edge.
                range: (_u, dataMin, dataMax) => {
                  if (dataMin == null || dataMax == null) return [0, 1];
                  const span = (dataMax as number) - (dataMin as number);
                  const pad = Math.max(span * 0.1, 1);
                  return [(dataMin as number) - pad, (dataMax as number) + pad];
                },
              },
            },
            axes: [
              {
                stroke: colors.axisStroke,
                grid:  { stroke: colors.gridStroke, width: 1 },
                ticks: { stroke: colors.gridStroke },
                font:  "11px Inter, system-ui, sans-serif",
              },
              {
                stroke: colors.axisStroke,
                grid:  { stroke: colors.gridStroke, width: 1 },
                ticks: { stroke: colors.gridStroke },
                font:  "11px Inter, system-ui, sans-serif",
                // size fits "-2.5 G" (6 chars × ~9px + breathing room).
                size: 56,
                gap:  4,
                // Pick unit once based on the largest tick magnitude,
                // then format every tick with the same unit + 1 decimal
                // (Wansight style: "0.5 G", "1.0 G", ..., "-2.5 G").
                values: (_u, ticks) => {
                  let maxAbs = 0;
                  for (const t of ticks) {
                    const a = Math.abs(t);
                    if (a > maxAbs) maxAbs = a;
                  }
                  const unit = pickAxisUnit(maxAbs);
                  return ticks.map((v) => fmtBpsAxis(v, unit));
                },
                // Adaptive tick generator: target one tick per ~38px of
                // chart height, so a 200px chart gets ~5 ticks per half
                // (in/out) instead of the previous fixed ~10. niceStep
                // snaps to {1, 2, 2.5, 5} × 10^N for clean values.
                splits: (u, _axisIdx, scaleMin, scaleMax) => {
                  const rng = scaleMax - scaleMin;
                  const plotH = (u.bbox?.height ?? 200) / window.devicePixelRatio;
                  const targetTicks = Math.max(4, Math.min(10, Math.round(plotH / 38)));
                  const target = rng / targetTicks;
                  const exp = Math.pow(10, Math.floor(Math.log10(target)));
                  const norm = target / exp;
                  let step: number;
                  if (norm < 1.5) step = 1 * exp;
                  else if (norm < 3) step = 2 * exp;
                  else if (norm < 7) step = 5 * exp;
                  else step = 10 * exp;
                  // Round start down to a step boundary so 0 is always
                  // a tick (which uPlot draws as the bold zero line).
                  const start = Math.ceil(scaleMin / step) * step;
                  const ticks: number[] = [];
                  for (let v = start; v <= scaleMax + step / 2; v += step) {
                    ticks.push(v);
                  }
                  return ticks;
                },
              },
            ],
            series: buildSeries(sensors),
          };
          plotRef.current = new uPlot(opts, data, ref.current);
          lastSeriesSig.current = seriesSig;
        } else if (plotRef.current) {
          plotRef.current.setData(data);
        }
      } catch {
        // chart stays blank on error
      }
    }

    load();
    // Auto-refresh suppressed when a custom range is active — the
    // window is anchored to absolute timestamps, polling would re-render
    // the same data wastefully.
    const id =
      refreshMs > 0 && !customRange ? window.setInterval(load, refreshMs) : undefined;

    function onResize() {
      if (plotRef.current && ref.current) {
        plotRef.current.setSize({ width: ref.current.clientWidth, height });
      }
    }
    window.addEventListener("resize", onResize);

    // ResizeObserver catches container size changes that window resize
    // misses (sidebar toggle, antd layout animations, etc.).
    let ro: ResizeObserver | undefined;
    if (ref.current) {
      ro = new ResizeObserver(onResize);
      ro.observe(ref.current);
    }

    return () => {
      cancelled = true;
      if (id !== undefined) window.clearInterval(id);
      window.removeEventListener("resize", onResize);
      ro?.disconnect();
    };
  }, [seconds, refreshMs, height, nonce, view, customRange, effective, scopeSensor]);

  // When the theme flips, drop the existing chart so the next render
  // builds it with the new axis colours.
  useEffect(() => {
    if (plotRef.current) {
      plotRef.current.destroy();
      plotRef.current = null;
    }
  }, [effective]);

  // Destroy uPlot only on unmount.
  useEffect(() => {
    return () => {
      if (plotRef.current) {
        plotRef.current.destroy();
        plotRef.current = null;
      }
    };
  }, []);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Controls row — inline in widget body (header is owned by DashboardGrid) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "3px 8px",
          borderBottom: "1px solid var(--line)",
          background: "var(--bg-2)",
          flexShrink: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <span style={{ fontSize: 10, color: "#8592b8" }}>View</span>
        <Select
          size="small"
          value={view}
          onChange={(v) => {
            setView(v);
            if (plotRef.current) { plotRef.current.destroy(); plotRef.current = null; }
          }}
          options={VIEW_OPTIONS}
          style={{ width: 130 }}
        />
        {sensorId === undefined && sensorOpts.length > 1 && (
          <>
            <span style={{ fontSize: 10, color: "#8592b8" }}>Sensor</span>
            <Select
              size="small"
              value={sensorFilter ?? 0}
              onChange={(v) => {
                setSensorFilter(v === 0 ? undefined : v);
                if (plotRef.current) { plotRef.current.destroy(); plotRef.current = null; }
              }}
              options={[{ label: "All sources", value: 0 }, ...sensorOpts]}
              style={{ width: 130 }}
            />
          </>
        )}
        <span style={{ fontSize: 10, color: "#8592b8" }}>Time Range</span>
        <Select
          size="small"
          // Show the sentinel when a custom range is active so the
          // dropdown reflects the picker state.
          value={customRange ? CUSTOM_RANGE_VALUE : seconds}
          onChange={(v) => {
            if (v === CUSTOM_RANGE_VALUE) {
              // Default the picker to the current relative window so
              // the operator gets a sensible starting point. They can
              // narrow / shift from there.
              const now = dayjs();
              setCustomRange([now.subtract(seconds, "second"), now]);
            } else {
              setSeconds(v);
              setCustomRange(null);
            }
            if (plotRef.current) { plotRef.current.destroy(); plotRef.current = null; }
          }}
          options={RANGES}
          style={{ width: 130 }}
        />
        {customRange && (
          <DatePicker.RangePicker
            size="small"
            showTime={{ format: "HH:mm" }}
            format="MM-DD HH:mm"
            value={customRange}
            allowClear={false}
            onChange={(v) => {
              if (v && v[0] && v[1]) {
                setCustomRange([v[0], v[1]]);
                if (plotRef.current) { plotRef.current.destroy(); plotRef.current = null; }
              }
            }}
            // Presets that match Wansight chart titles 1:1 so the
            // operator can tab between Wansight and sflow on the same
            // window without arithmetic.
            presets={[
              { label: "Last 1 h", value: [dayjs().subtract(1, "hour"), dayjs()] },
              { label: "Last 6 h", value: [dayjs().subtract(6, "hour"), dayjs()] },
              { label: "Last 12 h", value: [dayjs().subtract(12, "hour"), dayjs()] },
              { label: "Last 24 h", value: [dayjs().subtract(24, "hour"), dayjs()] },
              { label: "Today", value: [dayjs().startOf("day"), dayjs()] },
              { label: "Yesterday", value: [dayjs().subtract(1, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")] },
            ]}
            style={{ width: 280 }}
          />
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            // Bump our own re-fetch nonce AND broadcast to every other
            // chart on the page so the operator gets a synchronised pull.
            setNonce((n) => n + 1);
            window.dispatchEvent(new CustomEvent("sflow:refresh-all"));
          }}
          title="Refresh all charts now"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            background: "transparent",
            border: "none",
            padding: "2px 4px",
            fontSize: 10,
            color: "#36c5f0",
            cursor: "pointer",
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          <ReloadOutlined style={{ fontSize: 11 }} />
          Refresh
        </button>
        <Select
          size="small"
          value={refreshMs}
          onChange={(v) => setRefreshMs(v)}
          options={REFRESHES}
          style={{ width: 92 }}
        />
      </div>

      {/* Chart area */}
      <div style={{ flex: 1, position: "relative", padding: "6px 8px", minHeight: 0, background: "var(--bg-1)" }}>
        <div ref={ref} style={{ width: "100%" }} />
        {empty && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#8592b8",
              fontSize: 12,
            }}
          >
            no data yet — waiting for first 5s aggregation bin
          </div>
        )}
        {/* Data-stale / sensor-down banner. Three thresholds match the
            operational reality on a NetFlow installation:
              - ≤ 30 s: silent (true realtime).
              - 30 s – 5 min: yellow "Data lag" — likely just the
                NetFlow active-timeout export delay; nothing wrong.
              - > 5 min: red "Sensor offline" — sensor process or
                router export is dead and the operator should
                investigate.
            See plan §Bug 4 for why this matters: trimTrailingEmpty
            otherwise hides the missing data and the operator can stare
            at a frozen chart for hours without realising. */}
        {(() => {
          if (!lastDataAt) return null;
          const ageSecs = Math.max(0, Math.floor(Date.now() / 1000) - lastDataAt);
          if (ageSecs <= 30) return null;
          const offline = ageSecs > 300;
          return (
            <div
              style={{
                position: "absolute",
                top: 8,
                right: 12,
                padding: "4px 10px",
                borderRadius: 4,
                fontSize: 11,
                fontWeight: 600,
                color: offline ? "#fff" : "#1a1a1a",
                background: offline ? "#ef4444" : "#fbbf24",
                boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                pointerEvents: "auto",
              }}
              title={
                offline
                  ? "No flow data has arrived for over 5 minutes — check sfsensor and the router's flow exporter."
                  : "Data lag is normal for NetFlow because flows export at the active timeout (~60 s). A persistent lag above 5 min would indicate a problem."
              }
            >
              {offline ? "⚠ Sensor offline — last data " : "⏳ Data lag "}
              {fmtAgo(ageSecs)} ago
            </div>
          );
        })()}
      </div>
    </div>
  );
}
